import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import includes from 'lodash/includes'
import join from 'lodash/join'
import split from 'lodash/split'
import React, { FC, useCallback, useEffect, useState } from 'react'

import CloseIcon from '@Assets/SVG/Close'
import { LeadSubscribeForm } from '@Components/CMS/LeadMagnet/LeadSubscribeForm'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { NativeModal, NativeModalBody, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { Button } from '@CoreComponents/Buttons/Button'
import { storageAvailable } from '@Globals/Storage'

import styles from './LeadMagnet.module.scss'

const LS_HAS_LAST_SEEN_ANY_LEAD_MAGNET = 'hasLastSeenAnyLeadMagnet'
const LS_LEAD_MAGNETS_SEEN = 'leadMagnetsSeen'

const getHasShown = (identifyingLabel: string) => {
  if (!storageAvailable('localStorage')) {
    return false
  }

  // Check for any lead magnet last seen
  const lastSeen = window.localStorage.getItem(LS_HAS_LAST_SEEN_ANY_LEAD_MAGNET)
  if (!lastSeen) {
    return false
  }

  const now = new Date().getTime()
  const daysSinceSeen = (Number(now) - Number(lastSeen)) / 1000 / 60 / 60 / 24
  if (daysSinceSeen < 15) {
    return true
  }

  // Check if this lead magnet seen
  const allSeen = window.localStorage.getItem(LS_LEAD_MAGNETS_SEEN) || ''

  return includes(allSeen, identifyingLabel)
}

const defaultState = {
  /** real value is loaded from localStorage */
  hasShown: true,
  hiddenByUser: false,
  showBottomButton: true,
  showModal: false,
}

interface LeadMagnetProps {
  bottomButtonText: string
  buttonText: string
  desktopImage: string
  identifyingLabel: string
  mobileImage: string
  successMsg: string
  text: string
  title: string
}
export const LeadMagnet: FC<LeadMagnetProps> = ({
  bottomButtonText,
  identifyingLabel,
  ...formProps
}: LeadMagnetProps) => {
  const [
    {
      hasShown,
      hiddenByUser,
      showBottomButton,
      showModal,
    },
    setState,
  ] = useState(defaultState)

  const [threshold, setThreshold] = useState(9999)

  useEffect(() => {
    setThreshold(Math.min(
      window.innerHeight * 1.5,
      Math.max(document.body.getBoundingClientRect().height - 700, 0),
    ))
  }, [])

  useEffect(() => {
    setState(prev => ({ ...prev, hasShown: getHasShown(identifyingLabel) }))
  }, [identifyingLabel])

  const openOverlay = useCallback(() => {
    setState(prev => ({
      ...prev,
      hasShown: true,
      showBottomButton: false,
      showModal: true,
    }))

    if (!storageAvailable('localStorage')) {
      return
    }

    window.localStorage.setItem(LS_HAS_LAST_SEEN_ANY_LEAD_MAGNET, new Date().getTime().toString())

    // Set this lead magnet as seen
    const leadsSeen = split(window.localStorage.getItem(LS_LEAD_MAGNETS_SEEN), ',') ?? []

    if (!includes(leadsSeen, identifyingLabel)) {
      leadsSeen.push(identifyingLabel)
      window.localStorage.setItem(LS_LEAD_MAGNETS_SEEN, join(leadsSeen, ','))
    }

    setState(state => ({ ...state, hasShown: true }))
  }, [identifyingLabel])

  useEffect(() => {
    if (hasShown) {
      return undefined
    }

    const open = () => {
      if (!hasShown) {
        openOverlay()
      }
    }

    document.body.addEventListener('mouseleave', open, { passive: true })

    const timer = setTimeout(() => {
      open()
    }, 20000)

    return () => {
      document.body.removeEventListener('mouseleave', open)
      clearTimeout(timer)
    }
  }, [hasShown, openOverlay])

  useScrollPosition(
    ({ currPos }) => {
      if (hasShown) {
        return
      }

      const overThreshold = Math.abs(currPos.y) > threshold
      if (overThreshold) {
        openOverlay()
      }
    },
    [threshold, hasShown],
  )

  const onCloseModal = () => {
    setState(prev => ({ ...prev, showBottomButton: true, showModal: false }))
  }

  const onClickBottomButton = () => {
    openOverlay()
  }

  // hiddenByUser because `onCloseModal` toggles showBottomButton
  const showBottomNotHidden = showBottomButton && !hiddenByUser

  return (
    <>
      <NativeModal backdrop closeOnBackDrop closeOnEscape defaultOpen={showModal} onClose={() => onCloseModal()} prerender>
        <NativeModalHeader />
        <NativeModalBody>
          <div className={styles.formWrapper}>
            <LeadSubscribeForm
              closeModal={() => onCloseModal()}
              identifyingLabel={identifyingLabel}
              {...formProps}
            />
          </div>
        </NativeModalBody>
      </NativeModal>

      {showBottomNotHidden && (
        <div className={styles.bottom}>
          <div
            className={styles.closeBtn}
            onClick={() => setState(prev => ({ ...prev, hiddenByUser: true }))}
          >
            <SVGIcon icon={CloseIcon} size="14px" />
          </div>
          <Button
            className={styles.bottomButton}
            fillWidth
            innerSpacing="medium"
            onClick={() => onClickBottomButton()}
            theme="white"
          >
            {bottomButtonText}
          </Button>
        </div>
      )}
    </>
  )
}
